import { DESKTOP_BREAKPOINT, CURRENT_VERTICAL } from "@/constants/constants"
import { TRANSLATIONS } from "@/constants/translations"
import { extendTailwindMerge } from "tailwind-merge"
// eslint-disable-next-line import/no-relative-parent-imports
import { availableThemes } from "../../themes/index"

/**
 * Get Current Base URL
 * @returns {string} base url
 */
export function getBaseUrl() {
  return window.location.origin || ""
}

/**
 * Check if `extension` is running on `platform`
 * @info This property is being set in extension on initialization of extension in Preview
 * @returns {boolean}
 */
export function checkIsPlatform() {
  return Boolean(window.sessionStorage.getItem("isPlatform"))
}

/**
 * Checks if `value` is an empty object or array.
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is empty, else `false`.
 * @example
 * isEmpty(null) => true;
 * isEmpty(true) => true;
 * isEmpty(1) => true;
 * isEmpty([1, 2, 3]) => false;
 * isEmpty([]) => true;
 * isEmpty({ 'a': 1 }) => false
 * isEmpty({}) => true
 */
export function isEmpty(value) {
  if (value === null || value === undefined) {
    return true
  }

  if (Array.isArray(value)) {
    return !value.length
  }

  for (const key in value) {
    if (hasOwnProperty.call(value, key)) {
      return false
    }
  }

  return true
}

/**
 * Utility to generate random ID of certain character length given as argument
 *
 * This function creates a random string by converting a random number to a base-36 string (which includes numbers 0-9 and letters a-z),
 * and then extracting a substring of the specified length.
 * If no length is provided, the default length of 2 characters is used.
 *
 * @param {number} [length=2] The length of the ID to generate. Defaults to 2.
 * @returns {string} A random string ID of the specified length.
 *
 * @example
 * generateRandomId(2) => 'kp'
 */
export function generateRandomId(length = 2) {
  return Math.random()
    .toString(36)
    .substring(2, 2 + length)
}

/**
 * Generates a random alphanumeric string to be used as a filename.
 *
 * @param {number} [length=8] - The desired length of the generated filename. Defaults to 8 if not specified.
 * @returns {string} A randomly generated string consisting of uppercase letters, lowercase letters, and digits.
 *
 * @example
 * const filename = generateRandomFilename();
 * console.log(filename); // e.g., "aZ3xT9wLpQ1B"
 */
export function generateRandomFilename(length = 8) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  let result = ""
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * Converts a date string or timestamp into a formatted date string".
 *
 * @param {string|number|Date} inputDate - The date value to format. Can be a date string, timestamp, or Date object.
 * @returns {string} The formatted date string, or an error message if the input is invalid.
 *
 * @example
 * formatDate("2024-08-04T06:05:52.000+00:00") => "4 Aug, 2024"
 * formatDate() => "Invalid date"
 */
export function formatDate(inputDate) {
  const date = new Date(inputDate)

  // Check if the input is a valid date
  if (isNaN(date.getTime())) {
    return "Invalid date"
  }

  const day = date.getDate()
  const month = date.toLocaleString("default", { month: "short" })
  const year = date.getFullYear().toString().slice(-2) // Get the last two digits of the year

  return `${day} ${month} ${year}`
}

/**
 * Checks if the current window width is less than the desktop breakpoint,
 * indicating that the device is likely a mobile device.
 *
 * @returns {boolean} - Returns `true` if the window width is less than the desktop breakpoint, otherwise `false`.
 */
export function checkIsMobile() {
  return window.innerWidth < DESKTOP_BREAKPOINT
}

/**
 * Merges objects in an array that share the same `id` key, combining their properties.
 *
 * @param {Array<Object>} arrayData - The array of objects to be merged. Each object must have an `id` property.
 * @returns {Array<Object>} - A new array with merged objects, where each `id` is unique.
 *
 * @example
 * const data = [
 *   { id: 1, name: "Khyati", age: 25 },
 *   { id: 2, name: "Akanksha", age: 22 },
 *   { id: 1, name: "Sumit", hobby: "reading" },
 *   { id: 3, name: "Santosh", age: 30 },
 *   { id: 2, hobby: "dancing" }
 * ];
 *
 * const result = mergeDuplicateArrayOfObject(data);
 * // result will be:
 * // [
 * //   { id: 1, name: "Sumit", age: 25, hobby: "reading" },
 * //   { id: 2, name: "Akanksha", age: 22, hobby: "dancing" },
 * //   { id: 3, name: "Santosh", age: 30 }
 * // ]
 */
export function mergeDuplicateArrayOfObject(arrayData = []) {
  const mergedArrayDataObject = arrayData.reduce((acc, item) => {
    // If the id already exists, merge the objects
    if (acc[item.id]) {
      acc[item.id] = { ...acc[item.id], ...item }
    } else {
      // If the id doesn't exist, add the object
      acc[item.id] = { ...item }
    }
    return acc
  }, {})

  const mergedArrayData = Object.values(mergedArrayDataObject)

  return mergedArrayData
}

/**
 * Derives key product data and category information from a given product object.
 * Extracts specific attributes such as product ID, name, brand, image, URL, and alternate codes.
 * Additionally, extracts `uid` and `name` from category levels (L1 to L4) if available.
 *
 * @param {Object} product - The product object returned from the API.
 * @param {Object} [product.attributes] - The product's attributes, including the group-product-id.
 * @param {Array} [product.medias] - Array of media objects with the product images.
 * @param {Object} [product.brand] - Object containing the product brand information.
 * @param {string} [product.name] - The name of the product.
 * @param {string} [product.type] - The type of the product used to generate the URL.
 * @param {string} [product.slug] - The slug of the product used to generate the URL.
 * @param {string} [product.item_code] - The alternate code for the product.
 * @param {string} [product.uid] - The UID of the product.
 * @param {Object} [product.category_map] - Object containing category levels (L1, L2, L3, L4).
 * @param {Object} [product.category_map.l1] - Level 1 category object with `uid` and `name`.
 * @param {Object} [product.category_map.l2] - Level 2 category object with `uid` and `name`.
 * @param {Object} [product.category_map.l3] - Level 3 category object with `uid` and `name`.
 * @param {Object} [product.category_map.l4] - Level 4 category object with `uid` and `name`.
 *
 * @returns {Object} Derived product data containing essential product details and category information.
 * @returns {string} returns.productId - The derived product ID.
 * @returns {string} returns.productName - The name of the product.
 * @returns {string} returns.productBrand - The brand name of the product.
 * @returns {string} returns.productImage - The URL of the product's primary image.
 * @returns {string} returns.productURL - The URL generated for the product based on its type and slug.
 * @returns {string} returns.alternateCode1 - The item's alternate code (item_code).
 * @returns {string} returns.alternateCode2 - The item's UID.
 * @returns {Object} returns.categoryL1 - Category level 1 with `id`, and `name`.
 * @returns {Object} returns.categoryL2 - Category level 2 with `id`, and `name`.
 * @returns {Object} returns.categoryL3 - Category level 3 with `id`, and `name`.
 * @returns {Object} returns.categoryL4 - Category level 4 with `id`, and `name`.
 */
export function getDerivedProductValues(productData = {}) {
  const groupProductIds = productData?.attributes?.["group-product-id"]

  let newProductId

  if (Array.isArray(groupProductIds) && groupProductIds.length > 0) {
    newProductId = groupProductIds[0]
  } else {
    newProductId = productData.item_code
  }

  const derivedData = {
    productId: newProductId,
    productName: productData.name || "",
    productBrand: productData.brand?.name || "",
    productImage: productData.medias?.[0]?.url || "",
    productSlug: productData.slug,
    productURL: `${getBaseUrl()}/${productData.type}/${productData.slug}`,
    alternateCode1: productData.item_code || "",
    alternateCode2: productData.uid || "",
  }

  // Map category levels if available
  if (!isEmpty(productData.category_map)) {
    const { l1, l2, l3, l4 } = productData.category_map

    if (!isEmpty(l1)) {
      derivedData.categoryL1 = { id: l1.uid, name: l1.name }
    }
    if (!isEmpty(l2)) {
      derivedData.categoryL2 = { id: l2.uid, name: l2.name }
    }
    if (!isEmpty(l3)) {
      derivedData.categoryL3 = { id: l3.uid, name: l3.name }
    }
    if (!isEmpty(l4)) {
      derivedData.categoryL4 = { id: l4.uid, name: l4.name }
    }
  }
  return derivedData
}

/**
 * Checks the current vertical and returns an object indicating which vertical is active.
 *
 * @returns {Object} An object containing boolean values indicating the current active vertical:
 * - `isNetMeds`: True if the current vertical is "netmeds".
 * - `isJioMart`: True if the current vertical is "jiomart".
 * - `isTira`: True if the current vertical is "tira".
 * - `isRd`: True if the current vertical is "rd".
 */
export function checkCurrentVerticals() {
  return {
    netmeds: CURRENT_VERTICAL === "netmeds",
    jiomart: CURRENT_VERTICAL === "jiomart",
    tira: CURRENT_VERTICAL === "tira",
    rd: CURRENT_VERTICAL === "rd",
    ajio: CURRENT_VERTICAL === "ajio",
  }
}

/**
 * Formats a number with units 'k' for thousands, 'M' for millions and 'B' for billions.
 * @param {number} input - The number to format.
 * @returns {string} - The formatted number as a string.
 *
 * @example
 * formatToReadableNumber(12100); => "12.1k"
 * formatToReadableNumber("5000000"); => "5.0M"
 * formatToReadableNumber("1.2e9"); => "1.2B"
 * formatToReadableNumber("999"); => "999"
 * formatToReadableNumber("not a number"); => "not a number"
 */
export function formatToReadableNumber(input) {
  // Convert the input to a number if it's a string
  const num = typeof input === "string" ? parseFloat(input) : Number(input)

  // Check if the conversion was successful
  if (isNaN(num)) {
    return num
  }

  // Return immediately if less than 1000
  if (num < 1_000) return num.toString()

  const thresholds = [
    { value: 1_000_000_000, suffix: "B" },
    { value: 1_000_000, suffix: "M" },
    { value: 1_000, suffix: "k" },
  ]

  for (const { value, suffix } of thresholds) {
    if (num >= value) {
      return (num / value).toFixed(1) + suffix
    }
  }

  // This line is technically unreachable, but it's good practice to handle all cases.
  return num.toString()
}

/**
 * Translates a given key into the specified string, optionally replacing dynamic placeholders with provided parameters.
 *
 * @param {string} key - The key to look up the translation string.
 * @param {Object} [params={}] - An object containing dynamic values to replace placeholders in the translation string.
 * @returns {string} - The translated string with placeholders replaced by dynamic values, if any.
 *
 * @example
 * // Assuming translations.jiomart.greeting = "Hello, {name}!"
 * translate("greeting", { name: "Sumit" }); // Returns: "Hello, Sumit!"
 *
 * @example
 * // Fallback to common translations if the key is not found in the vertical-specific section
 * translate("commonKey", {}); // Returns the common translation string.
 */
export function translate(key, params) {
  // Check in the vertical-specific section, fallback to the common section
  let translatedText =
    TRANSLATIONS[CURRENT_VERTICAL]?.[key] || TRANSLATIONS.common?.[key] || ""

  // Replace dynamic values only if params is provided and not empty
  if (params && Object.keys(params).length > 0) {
    Object.keys(params).forEach((param) => {
      translatedText = translatedText.replace(`{${param}}`, params[param])
    })
  }

  return translatedText
}

export const classMerge = extendTailwindMerge({
  prefix: "fe-",
  override: {
    theme: {
      colors: [
        "inherit",
        "current",
        "transparent",
        ...Object.keys(availableThemes[CURRENT_VERTICAL].colors),
      ],
      fontSize: [...Object.keys(availableThemes[CURRENT_VERTICAL].fontSize)],
      screens: [...Object.keys(availableThemes[CURRENT_VERTICAL].screens)],
    },
  },
  extend: {
    theme: {
      spacing: [...Object.keys(availableThemes[CURRENT_VERTICAL].spacing)],
      boxShadow: [...Object.keys(availableThemes[CURRENT_VERTICAL].boxShadow)],
      borderRadius: [
        ...Object.keys(availableThemes[CURRENT_VERTICAL].borderRadius),
      ],
      borderWidth: [...Object.keys(availableThemes[CURRENT_VERTICAL].spacing)],
      zIndex: [...Object.keys(availableThemes[CURRENT_VERTICAL].zIndex)],
    },
  },
})

/**
 * Checks if the current device is running iOS.
 *
 * @returns {boolean} `true` if the device is iOS, otherwise `false`.
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent)
}

/**
 * Checks whether the current device is running Android.
 *
 * @returns {boolean} Returns `true` if the user agent corresponds to an Android device, otherwise `false`.
 */
export function isAndroid() {
  return /Android/i.test(window.navigator.userAgent)
}

/**
 * Determines whether the current device is a mobile device (iOS or Android).
 *
 * @returns {boolean} `true` if the device is either iOS or Android, otherwise `false`.
 */
export function isMobileApp() {
  return isIOS() || isAndroid()
}

/**
 * Sends data to a native mobile application.
 *
 * The function encodes the given payload in Base64 and sends it to
 * the React Native or Native app handler (Android or iOS).
 *
 * @param {Object} payload - The data to be sent to the app.
 */
export function sendToApp(payload) {
  if (typeof window === "undefined") {
    return
  }

  const encodedData = window.btoa(JSON.stringify(payload))

  // Netmeds Android App handler
  if (window.__fyndKit) {
    window.__fyndKit?.openAction(JSON.stringify(payload))

    return
  }

  // React Native handler -  JCP mobile app is written in React Native and this method is used to communicate from web to app.
  if (window.ReactNativeWebView?.postMessage) {
    window.ReactNativeWebView.postMessage(encodedData)

    return
  }

  // Android handler
  if (window.android?.__externalCall) {
    window.android.__externalCall(encodedData)

    return
  }

  // Generic handler (if available)
  if (window.__externalCall) {
    window.__externalCall(encodedData)

    return
  }

  // Netmeds iOS App handler
  if (isIOS() && window.webkit?.messageHandlers?.openAction) {
    window.webkit.messageHandlers.openAction.postMessage(
      JSON.stringify(payload),
    )

    return
  }

  // iOS handler
  if (isIOS() && window.webkit?.messageHandlers?.callback) {
    window.webkit.messageHandlers.callback.postMessage(encodedData)

    return
  }
}

export function customerLoginHandler(accountsData) {
  if (!accountsData) {
    return
  }

  if (isMobileApp() && localStorage.getItem("__application")) {
    sendToApp({ type: "login", params: {} })
  } else if (window?.ucpRetailAuth?.openLogin) {
    window.ucpRetailAuth.openLogin(accountsData.ucpredirectURL)
  } else {
    accountsData.openLogin()
  }
}

/**
 * Returns the pluralized form of a word based on the provided count.
 *
 * Adds an "s" to the end of the word if the count is not equal to 1 or 0.
 * Note: This handles only simple plural forms (e.g., "rating" → "ratings").
 *
 * @param {string} word - The word to pluralize.
 * @param {number} count - The number to base the pluralization on.
 * @returns {string} The correctly pluralized word.
 */
export function pluralize(word, count) {
  return `${word}${count !== 1 && count !== 0 ? "s" : ""}`
}

export function dataURItoBlob(dataURI) {
  // Split the data URI
  const [header, base64String] = dataURI.split(",")

  // Extract the MIME type from the header
  const mimeMatch = header.match(/:(.*?);/)
  const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg"

  // Decode base64 string
  const byteString = atob(base64String)
  const arrayBuffer = new ArrayBuffer(byteString.length)
  const int8Array = new Uint8Array(arrayBuffer)
  for (let i = 0; i < byteString.length; i++) {
    int8Array[i] = byteString.charCodeAt(i)
  }

  const imageExtension = mimeType.split("/")[1] || "jpeg"
  const imageName = `${generateRandomFilename(8)}.${imageExtension}`

  // Create Blob and File
  const imageBlob = new Blob([int8Array], { type: mimeType })
  const imageFile = new File([imageBlob], imageName, {
    type: mimeType,
  })

  return imageFile
}

/**
 * ## This is temporary util to handle backward compatibility, in future it should be removed when not needed ##
 * Checks if the version stored in localStorage is greater than equal to the target version.
 * The default target version is '10.0.6v223'.
 *
 * @param {string} [targetVersion='10.0.6v223'] - The version to compare against.
 * @param {string} [storageKey='__version'] - The key in localStorage to fetch the version from.
 * @returns {boolean} True if stored version is greater than target, false otherwise.
 */

export function isAppVersionGreaterThanOrEqual(
  targetVersion = "10.0.6v223",
  storageKey = "__version",
) {
  const storedVersion = localStorage.getItem(storageKey)
  if (!storedVersion) return false

  /**
   * Converts any separator (non-digit) to a dot, then parses version segments as numbers.
   * E.g., "10.0.6_223" or "10.0.6v223" becomes [10, 0, 6, 223]
   * @param {string} version
   * @returns {number[]}
   */
  const parseVersion = (version) => {
    return version
      .replace(/[^0-9]+/g, ".") // Replace non-digit sequences with dots
      .split(".") // Split into segments
      .filter(Boolean) // Remove empty strings
      .map(Number) // Convert to numbers
  }

  const currentParts = parseVersion(storedVersion)
  const targetParts = parseVersion(targetVersion)

  for (let i = 0; i < Math.max(currentParts.length, targetParts.length); i++) {
    const current = currentParts[i] || 0
    const target = targetParts[i] || 0

    if (current > target) return true
    if (current < target) return false
  }

  return true // All parts are equal
}
